<template>
  <div :class="getItemTableContainerClass">
    <ItemTable
      v-if="ssoFilter"
      ref="itemTable"
      :boundItemsUrl="boundItemsUrl"
      bulkActionText="export metadata"
      :columns="columns[panelType]"
      :details="inMainPanel"
      :gettingStartedItemType="gettingStartedItemType"
      :handleBulkAction="handleBulkAction"
      :handleBulkDelete="handleBulkDelete"
      :handleMainAction="handleMainAction"
      :handleRowClick="handleRowClick"
      :hasSelectColumn="true"
      :inDetailsAsidePanel="!inMainPanel"
      :isBulkMainActionDisabled="isBulkMainActionDisabled"
      :itemType="itemType"
      :linkText="applicationLinkText"
      :reportType="reportType"
      :route="route"
      :selectionItemText="applicationItemText"
      :showBound="false"
      :showBulkAction="inMainPanel"
      :showBulkDelete="inMainPanel"
      :showMainAction="inMainPanel"
      :showSearchForm="true"
      :showSelectedItemsCount="showSelectedItemsCount"
      :useDropDownFilterComponent="useDropDownFilterComponent"
    />
    <ItemTable
      v-else
      ref="itemTable"
      :boundItemsUrl="boundItemsUrl"
      bulkActionText="export metadata"
      :columns="columns[panelType]"
      :details="inMainPanel"
      :gettingStartedItemType="gettingStartedItemType"
      :handleBulkAction="handleBulkAction"
      :handleBulkDelete="handleBulkDelete"
      :handleMainAction="handleMainAction"
      :handleRowClick="handleRowClick"
      :hasSelectColumn="true"
      :inDetailsAsidePanel="!inMainPanel"
      :isBulkMainActionDisabled="isBulkMainActionDisabled"
      :itemType="itemType"
      :linkText="applicationLinkText"
      :reportType="reportType"
      :route="route"
      :selectionItemText="applicationItemText"
      :showBound="false"
      :showBulkAction="inMainPanel"
      :showBulkDelete="inMainPanel"
      :showMainAction="inMainPanel"
      :showSearchForm="true"
      :showSelectedItemsCount="showSelectedItemsCount"
    />
  </div>
</template>

<script>
import { extend } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import pluralize from 'pluralize';

import * as Constants from 'client/util/Constants';
import { ReportTypes } from 'client/Reports/ReportMetadata';
import { applicationSsoTypes } from 'client/util/Constants/ApplicationConstants';
import { featureFlags } from 'client/store/modules/FeatureFlagsModule';
import { getAppItemTableColumnSettings } from 'client/applications/ApplicationItemTable/ApplicationItemTableColumns';
import { notify } from 'client/util';

import ApplicationExpiration from 'client/components/ItemTable/BodyComponents/ApplicationExpiration';
import ApplicationFeatures from 'client/components/ItemTable/BodyComponents/ApplicationFeatures.vue';
import ApplicationShownInUserPortal from 'client/components/ItemTable/BodyComponents/ApplicationShownInUserPortal.vue';
import DeleteConfirmationModal from 'components/Modal/ModalComponents/DeleteConfirmationModal.vue';
import ItemTable from 'client/components/ItemTable/ItemTable.vue';
import ItemTableComputedMixin from 'client/components/ItemTable/mixins/ItemTableComputedMixin';
import ItemTableMethodsMixin from 'client/components/ItemTable/mixins/ItemTableMethodsMixin';
import ItemTablePropsMixin from 'client/components/ItemTable/mixins/ItemTablePropsMixin';
import ModalEventBus from 'components/Modal/ModalEventBus';
import Util from 'client/util/Util';

const {
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeDisplayNames,
  ItemTypeRoutes,
  StoreModuleMap,
} = Constants;
const { SAML } = applicationSsoTypes;
const { error, success } = Constants.default;

const settingsModule = StoreModuleMap[ItemTypeConsts.settings];
const applicationCollection = StoreModuleMap[ItemTypeConsts.application];
const { ssoFilter } = featureFlags;

export default {
  name: 'ApplicationItemTable',

  components: {
    ItemTable,
  },

  mixins: [
    ItemTableComputedMixin,
    ItemTableMethodsMixin,
    ItemTablePropsMixin,
  ],

  props: {
    itemType: {
      default: ItemTypeConsts.application,
      type: String,
    },
    panelType: {
      default: ItemTableConsts.panelType.mainPanel,
      type: String,
    },
    showSelectedItemsCount: {
      default: true,
      type: Boolean,
    },
  },

  data() {
    const displayName = ItemTypeDisplayNames[this.itemType];
    const columns = {};
    const appItemTableColumnSettings = getAppItemTableColumnSettings();

    const defaultColumns = [
      appItemTableColumnSettings.status,
      appItemTableColumnSettings.name,
      appItemTableColumnSettings.displayLabel,
      {
        bodyComponent: ApplicationFeatures,
        bodyComponentProps: {
          itemType: this.itemType,
        },
        headingText: 'Supported Functionality',
      },
      // TODO: Display SSO when requested
    ];

    /*
    ID-249: TODO: remove comment when feature flag is removed.
      const showInUserPortalColumn = {
      bodyComponent: ApplicationShownInUserPortal,
      bodyComponentProps: {
        itemType: this.itemType,
      },
      headingText: 'Show in User Portal',
    }; */

    const idpCertificateStatusColumn = {
      bodyComponent: ApplicationExpiration,
      headingText: 'IdP Certificate Status',
      name: 'IdpCertificateStatus',
    };

    // ID-249: TODO: remove comment when feature flag is removed.
    /* columns[ItemTableConsts.panelType.groupAsidePanel] = defaultColumns.concat(
      [showInUserPortalColumn],
    ); */

    // ID-249: TODO: Remove next line when feature flag is removed.
    columns[ItemTableConsts.panelType.groupAsidePanel] = defaultColumns;

    columns[ItemTableConsts.panelType.mainPanel] = defaultColumns.concat(
      [idpCertificateStatusColumn],
    );

    return {
      columns,
      applicationItemText: {
        label: 'bound',
        plural: pluralize(displayName),
        singular: displayName,
      },
      applicationLinkText: 'Applications',
      deleteLoading: false,
      reportType: ReportTypes.userToSso,
    };
  },

  computed: {
    ...mapGetters(applicationCollection, {
      selectedItems: 'getSelectedItems',
      collection: 'getCollection',
    }),

    ...mapState('FeatureFlagsModule', {
      [ssoFilter]: state => state.betaFeatures[ssoFilter],
    }),

    ...mapState(settingsModule, [
      'orgId',
    ]),

    isBulkMainActionDisabled() {
      // if only bookmarks are selected, disable export metadata button
      // if there are any saml apps selected, enable button
      return this.selectedItems.every(id => !this.checkCollectionForSaml(id));
    },

    isShowInUserPortalFFOn() {
      return this.$hasFeatureFlag(this.$featureFlags.ssoShowInUserPortal);
    },

    route() {
      return ItemTypeRoutes[ItemTypeConsts.sso];
    },

  },
  // ID-249: TODO: Remove created hook when feature flag is removed.
  created() {
    if (this.$hasFeatureFlag(this.$featureFlags.ssoShowInUserPortal)) {
      this.columns[ItemTableConsts.panelType.groupAsidePanel].push({
        bodyComponent: ApplicationShownInUserPortal,
        bodyComponentProps: {
          itemType: this.itemType,
        },
        headingText: 'Show in User Portal',
      });
    }
  },

  beforeCreate() {
    const collection = applicationCollection;
    extend(
      this.$options.computed,
      mapGetters(collection, {
        selectedItems: 'getSelectedItems',
        collection: 'getCollection',
      }),
    );

    extend(
      this.$options.methods,
      mapActions(collection, [
        'deleteItems',
      ]),
    );
  },

  methods: {
    ...mapActions(applicationCollection, [
      'deleteItems',
    ]),

    checkCollectionForSaml(id) {
      return this.collection.some(obj => obj.id === id && obj.sso?.type === SAML);
    },

    deleteSelectedApplications() {
      this.deleteLoading = true;
      this.deleteItems(this.selectedItems).then((results) => {
        notify(success, `${results.deletedIds.length} ${pluralize('Application', results.deletedIds.length)} successfully deleted.`);
      }).catch(() => {
        notify(error, `There was an error deleting the selected ${pluralize('Application', this.selectedItems.length)}`);
      }).finally(() => {
        this.deleteLoading = false;
        ModalEventBus.close();
      });
    },

    handleBulkAction() {
      this.selectedItems.forEach((id) => {
        // only sso apps pass conditional, no bookmarks
        if (this.checkCollectionForSaml(id)) {
          const path = `/api/organizations/${this.orgId}/applications/${id}/metadata.xml`;
          if (window && window.open) {
            window.open(path, '_blank');
          }
        }
      });
    },

    handleBulkDelete() {
      ModalEventBus.open({
        component: DeleteConfirmationModal,
        componentProps: {
          bodyText: `Are you sure you would like to delete the selected ${pluralize('application', this.selectedItems.length)}?`,
          continueText: 'yes, delete',
          enableMainAction: true,
          handleContinue: this.deleteSelectedApplications,
          headerText: `Delete ${pluralize('Application', this.selectedItems.length)}`,
          loading: this.deleteLoading,
          mainActionAttention: true,
        },
      });
    },

    handleMainAction() {
      Util.redirect(`${this.route}/choose`);
    },
  },
};
</script>
